/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  FC,
  MouseEvent,
  ReactNode,
} from "react";
import { VariantProps, cva } from "cva";
import { Link } from "react-router-dom";
import type { LinkProps } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";

type BaseProps = {
  children: ReactNode;
  className?: string;
  isLoading?: boolean;
  childClassName?: string;
  hasShadow?: boolean;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"] | Function;
} & VariantProps<typeof buttonStyles>;

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: "button" | undefined;
    shouldPreventDefault?: boolean;
  };
type ButtonAsLink = BaseProps &
  Omit<LinkProps, keyof BaseProps> & {
    as: "link";
  };
type ButtonAsExternal = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: "externalLink";
  };
type ButtonAsDiv = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLDivElement>, keyof BaseProps> & {
    as: "div";
    disabled?: boolean;
  };

type ButtonProps =
  | ButtonAsButton
  | ButtonAsLink
  | ButtonAsExternal
  | ButtonAsDiv;

const buttonStyles = cva(
  "flex items-center justify-center transition duration-300 relative font-bold",
  {
    variants: {
      variant: {
        primary: [
          "outline-none -text--theme-colors-action-primary-default-fg -bg--theme-colors-action-primary-default-bg",
          "hover:-bg--theme-colors-action-primary-hover-bg hover:-text--theme-colors-action-primary-hover-fg",
          "active:-text--theme-colors-action-primary-clicked-fg active:-bg--theme-colors-action-primary-clicked-bg",
          "focus:[box-shadow:0px_0px_0px_2px_#CCC,_0px_0px_0px_1px_#2B2B2B;] -text--theme-colors-action-primary-focused-fg focus:-bg--theme-colors-action-primary-focused-bg",
          "disabled:-text--theme-colors-action-primary-disabled-fg disabled:-bg--theme-colors-action-primary-disabled-bg",
          "aria-disabled:-text--theme-colors-action-primary-disabled-fg aria-disabled:-bg--theme-colors-action-primary-disabled-bg -rounded-button",
        ],
        secondary: [
          "outline-none -text--theme-colors-action-secondary-default-fg -bg--theme-colors-action-secondary-default-bg",
          "hover:-bg--theme-colors-action-secondary-hover-bg hover:-text--theme-colors-action-secondary-hover-fg",
          "active:-text--theme-colors-action-secondary-clicked-fg active:-bg--theme-colors-action-secondary-clicked-bg",
          "focus:[box-shadow:0px_0px_0px_2px_#CCC,_0px_0px_0px_1px_#2B2B2B;] focus:-text--theme-colors-action-secondary-focused-fg focus:-bg--theme-colors-action-secondary-focused-bg",
          "disabled:-text--theme-colors-action-secondary-disabled-fg disabled:-bg--theme-colors-action-secondary-disabled-fg",
          "aria-disabled:-text--theme-colors-action-secondary-disabled-fg aria-disabled:-bg--theme-colors-action-secondary-disabled-fg -rounded-button",
        ],
        tertiary: [
          "outline-none -text--theme-colors-action-tertiary-default-fg -bg--theme-colors-action-tertiary-default-bg border -border--theme-colors-action-tertiary-default-border",
          "hover:-bg--theme-colors-action-tertiary-hover-bg hover:-text--theme-colors-action-tertiary-hover-fg",
          "active:-text--theme-colors-action-tertiary-clicked-fg active:-bg--theme-colors-action-tertiary-clicked-bg",
          "focus:[box-shadow:0px_0px_0px_2px_#CCC,_0px_0px_0px_1px_#2B2B2B;] focus:-text--theme-colors-action-tertiary-focused-fg focus:-bg--theme-colors-action-tertiary-focused-bg",
          "disabled:-text--theme-colors-action-tertiary-disabled-fg disabled:-bg--theme-colors-action-tertiary-disabled-bg",
          "aria-disabled:-text--theme-colors-action-tertiary-disabled-fg aria-disabled:-bg--theme-colors-action-tertiary-disabled-bg -rounded-button",
        ],
        link: [
          "outline-none -text--theme-colors-action-link-default-fg -bg--theme-colors-action-link-default-bg underline !p-0",
          "hover:-bg--theme-colors-action-link-hover-bg hover:-text--theme-colors-action-link-hover-fg",
          "active:-text--theme-colors-action-link-clicked-fg active:-bg--theme-colors-action-link-clicked-bg",
          "focus:-text--theme-colors-action-link-focused-fg focus:-bg--theme-colors-action-link-focused-bg",
          "disabled:-text--theme-colors-action-link-disabled-fg disabled:-bg--theme-colors-action-link-disabled-bg",
          "aria-disabled:-text--theme-colors-action-link-disabled-fg aria-disabled:-bg--theme-colors-action-link-disabled-bg -rounded-button",
        ],
        danger: [
          "outline-none -text--theme-colors-action-danger-default-fg -bg--theme-colors-action-danger-default-bg",
          "hover:-bg--theme-colors-action-danger-hover-bg hover:-text--theme-colors-action-danger-hover-fg",
          "active:-text--theme-colors-action-danger-clicked-fg active:-bg--theme-colors-action-danger-clicked-bg",
          "focus:[box-shadow:0px_0px_0px_2px_#CCC,_0px_0px_0px_1px_#2B2B2B;] focus:-text--theme-colors-action-danger-focused-fg focus:-bg--theme-colors-action-danger-focused-bg",
          "disabled:-text--theme-colors-action-danger-disabled-fg disabled:-bg--theme-colors-action-danger-disabled-bg",
          "aria-disabled:-text--theme-colors-action-danger-disabled-fg aria-disabled:-bg--theme-colors-action-danger-disabled-bg -rounded-button",
        ],
        "danger-secondary": [
          "outline-none -text--theme-colors-action-sec-danger-default-fg -bg--theme-colors-action-sec-danger-default-bg",
          "hover:-bg--theme-colors-action-sec-danger-hover-bg hover:-text--theme-colors-action-sec-danger-hover-fg",
          "active:-text--theme-colors-action-sec-danger-clicked-fg active:-bg--theme-colors-action-sec-danger-clicked-bg",
          "focus:[box-shadow:0px_1px_2px_0px_rgba(16, 24, 40, 0.05),_0px_0px_0px_2px_#CBCBCB,_0px_0px_0px_1px_#2B2B2B;] focus:-text--theme-colors-action-sec-danger-focused-fg focus:-bg--theme-colors-action-sec-danger-focused-bg",
          "disabled:-text--theme-colors-action-sec-danger-disabled-fg disabled:-bg--theme-colors-action-sec-danger-disabled-bg",
          "aria-disabled:-text--theme-colors-action-sec-danger-disabled-fg aria-disabled:-bg--theme-colors-action-sec-danger-disabled-bg -rounded-button",
        ],
        "danger-ghost": [
          "outline-none -text--theme-colors-action-ghost-danger-default-fg -bg--theme-colors-action-ghost-danger-default-bg",
          "hover:-bg--theme-colors-action-ghost-danger-hover-bg hover:-text--theme-colors-action-ghost-danger-hover-fg",
          "active:-text--theme-colors-action-ghost-danger-clicked-fg active:-bg--theme-colors-action-ghost-danger-clicked-bg",
          "focus:-text--theme-colors-action-ghost-danger-focused-fg focus:-bg--theme-colors-action-ghost-danger-focused-bg",
          "disabled:-text--theme-colors-action-ghost-danger-disabled-fg disabled:-bg--theme-colors-action-ghost-danger-disabled-bg",
          "aria-disabled:-text--theme-colors-action-ghost-danger-disabled-fg aria-disabled:-bg--theme-colors-action-ghost-danger-disabled-bg -rounded-button",
        ],
        ghost: [
          "outline-none -text--theme-colors-action-ghost-default-fg -bg--theme-colors-action-ghost-default-bg",
          "hover:-bg--theme-colors-action-ghost-hover-bg hover:-text--theme-colors-action-ghost-hover-fg",
          "active:-text--theme-colors-action-ghost-clicked-fg active:-bg--theme-colors-action-ghost-clicked-bg",
          "focus:-text--theme-colors-action-ghost-focused-fg focus:-bg--theme-colors-action-ghost-focused-bg",
          "disabled:-text--theme-colors-action-ghost-disabled-fg disabled:-bg--theme-colors-action-ghost-disabled-bg",
          "aria-disabled:-text--theme-colors-action-ghost-disabled-fg aria-disabled:-bg--theme-colors-action-ghost-disabled-bg -rounded-button",
        ],
        "ghost-unstyled": [
          "outline-none -text--theme-colors-action-ghost-default-fg !p-0",
          "hover:-text--theme-colors-action-ghost-hover-fg",
          "active:-text--theme-colors-action-ghost-clicked-fg",
          "focus:-text--theme-colors-action-ghost-focused-fg",
          "disabled:-text--theme-colors-action-ghost-disabled-fg",
          "aria-disabled:-text--theme-colors-action-ghost-disabled-fg -rounded-button",
        ],
      },
      size: {
        xs: "py-[5px] px-[14px] text-[14px] leading-5 min-h-[30px]",
        sm: "py-2 px-4 text-[14px] leading-5 min-h-[36px]",
        md: "py-2 px-[18px] text-[16px] leading-6 min-h-[40px]",
        lg: "py-[9px] px-5 text-[18px] leading-6 min-h-[48px]",
        xl: "py-[9px] px-5 text-[18px] leading-7 min-h-[50px]",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
      shadow: {
        true: "[box-shadow:0px_9px_49px_#b5c1cd;]",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "lg",
      fullWidth: false,
      shadow: false,
    },
  },
);

const Button: FC<ButtonProps> = (props) => {
  const { getEventRoute } = useEventRouting();
  if (props.as === "link") {
    const {
      as,
      className,
      childClassName,
      children,
      variant,
      hasShadow,
      size,
      isLoading,
      onClick,
      fullWidth,
      to,
      ...rest
    } = props;
    return (
      <Link
        to={getEventRoute(to)}
        className={cn(
          buttonStyles({ variant, shadow: hasShadow, size: size, fullWidth }),
          className,
        )}
        {...rest}
      >
        <div className={childClassName}>{children}</div>
      </Link>
    );
  } else if (props.as === "div") {
    const {
      as,
      className,
      childClassName,
      disabled,
      children,
      variant,
      hasShadow,
      size,
      isLoading,
      onClick,
      fullWidth,
      ...rest
    } = props;
    return (
      <div
        {...rest}
        aria-disabled={disabled}
        className={cn(
          buttonStyles({ variant, shadow: hasShadow, size: size, fullWidth }),
          "cursor-pointer",
          className,
        )}
      >
        <div className={childClassName}>{children}</div>
      </div>
    );
  } else if (props.as === "externalLink") {
    const {
      as,
      className,
      childClassName,
      children,
      variant,
      hasShadow,
      size,
      isLoading,
      onClick,
      fullWidth,
      ...rest
    } = props;
    return (
      <a
        {...rest}
        className={cn(
          buttonStyles({ variant, shadow: hasShadow, size: size, fullWidth }),
          className,
        )}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className={childClassName}>{children}</div>
      </a>
    );
  } else if (props.as === "button" || props.as === undefined) {
    const {
      as,
      className,
      childClassName,
      children,
      variant,
      hasShadow,
      size,
      isLoading,
      fullWidth,
      shouldPreventDefault,
      ...rest
    } = props;
    const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (shouldPreventDefault || shouldPreventDefault === undefined)
        e.preventDefault();
      if (isLoading || props.disabled) return;
      if (props.onClick) props.onClick(e);
    };
    return (
      <button
        {...rest}
        onClick={handleOnClick}
        className={cn(
          buttonStyles({ variant, shadow: hasShadow, size: size, fullWidth }),
          className,
        )}
      >
        {isLoading && (
          <span className="absolute start-3 self-center">
            <svg
              className="animate-spin -ms-1 me-3 h-5 w-5 text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </span>
        )}
        <div className={childClassName}>{children}</div>
      </button>
    );
  }
};

export default Button;
